'use client'

/**
 * The sidebar badge of Deepfake > Replies: how many conversations wait for an answer.
 *
 * HumanFirewall's Deepfake Simulation is the authorised, consented awareness exercise an organisation runs against
 * its OWN employees. The number is `needsReply` of GET /v1/deepfake/replies/counts, aggregated in SQL (the browser
 * never counts rows) and refreshed every minute while the tab is visible. It is not drawn at zero or while unknown:
 * absence of a number is not a promise that nothing waits, so a failed read shows nothing rather than a zero.
 *
 * Wire it into the sidebar as the item's `badge` (see `withPolicyApprovalBadge` in components/app-sidebar.tsx for the
 * shape): `badge: <DeepfakeRepliesNavBadge />` on the `/deepfake/replies` entry.
 */
import { useDeepfakeReplyCounts } from '@/hooks/api/use-deepfake-replies'
import { useTranslation } from '@/lib/i18n'
import { formatLearnerNumber } from '@/components/user-portal/learner-i18n'

/** The most the badge writes: beyond it the badge says "99+", as every badge of the app does. */
const BADGE_CAP = 99

export function DeepfakeRepliesNavBadge() {
  const { t, locale } = useTranslation()
  const { data } = useDeepfakeReplyCounts()
  if (!data || data.needsReply <= 0) return null
  const shown = data.needsReply > BADGE_CAP ? `${formatLearnerNumber(BADGE_CAP, locale)}+` : formatLearnerNumber(data.needsReply, locale)
  return (
    <span
      data-deepfake-replies-badge=""
      title={t('deepfake.replies.nav.badge', { count: data.needsReply })}
      aria-label={t('deepfake.replies.nav.badge', { count: data.needsReply })}
      className="inline-flex min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-xs font-semibold tabular-nums text-white"
    >
      {shown}
    </span>
  )
}
